/**
 * Store: Localisation
 * ------------------------------------------------------------------------------
 * Global store for currency and locale.
 *
 * @namespace useLocaleStore
 */
import { defineStore } from 'pinia'
import Cookies from 'js-cookie'

import { cookieNamespace, storageNamespace } from '~/helpers/storage-namespaces'
import { getCookieValue } from '~/helpers/utils'

/**
 * Export a Locale global state.
 */
export const useLocaleStore = defineStore({
  id: 'locale',

  state: () => {
    return {
      continentCode: mint?.shop?.continentCode || false,
      countryCode: mint?.shop?.country || false,
      countryName: mint?.shop?.countryName || false,
      currencyCode: mint?.shop?.currency || false,
      currencySymbol: mint?.shop?.currencySymbol || false,
      languageCode: mint?.shop?.language || false,
      unitSystem: mint?.shop?.unitSystem || false,
      detected: {
        countryCode: false,
        countryName: false,
        languageCode: false,
        languageName: false,
        storeCountryCode: mint?.shop?.country || false,
      },
      states: {
        hasSwitchedMarkets: false,
        isCorrectMarket: null,
        isDefaultMarket: null,
        isSupportedMarket: null,
        locationSet: false,
      },
      suggested: {
        shouldSuggest: false,
        target: false,
      },
      defaults: {
        continentCode: 'EU',
        countryCode: 'GB',
        countryName: 'United Kingdom',
        currencyCode: 'GBP',
        currencySymbol: '£',
        languageCode: 'en',
        unitSystem: 'metric',
      },
    }
  },

  actions: {

    /**
     * Query Shopify for the customers location.
     */
    queryLocale() {
      const newState = {}
      const detected = this.detected

      /**
       * Get cached geolocation.
       * - First is set by this file.
       * - Second is set by Shopify.
       */
      let cachedGeolocation = JSON.parse(getCookieValue(cookieNamespace.geolocation))

      const shopifyCountryCode = mint.shop.country

      /**
       * Shopify will store the countryCode for the current market in a cookie,
       * but the Liquid country object should be more reliable.
       * - Compare that value with our cached value
       * - If they are different, the customer has recently switched market,
       *   therefore we should flush the cache.
       */
      let shouldClearCache = Boolean(shopifyCountryCode !== cachedGeolocation.storeCountryCode)

      if (mint.flags.dev.disableGeolocationCache) {
        shouldClearCache = true
      }

      /**
       * Option to disable caching during development.
       */
      if (shouldClearCache) {
        cachedGeolocation = false
        Cookies.remove(cookieNamespace.geolocation)
        sessionStorage.removeItem(storageNamespace.pinia.locale)
      }

      /**
       * If there exists a cached value, use that instead.
       * - Realistically, customers will not be frequently switching geolocation.
       */
      if (
        !mint.flags.dev.disableGeolocationCache &&
        (typeof cachedGeolocation !== 'boolean' && cachedGeolocation.countryCode)
      ) {
        newState.detected = cachedGeolocation
        newState.states = this.getUpdatedStates(newState.detected, shouldClearCache)

        this.setLocale(newState)
        return
      }

      /**
       * Endpoint will 401 when requested via HMR link.
       * - Prevent request and fallback to defaults.
       */
      if (mint.env.localhost) {
        window.console.warn('[Mint Geolocation] Geolocation is not available in localhost HMR mode. To use geolocation methods during development, access the theme via the permanent theme URL from Shopify CLI (?preview_theme_id=XXX). Falling back to default geolocation values')

        newState.detected = {
          'countryCode': this.defaults.countryCode,
          'countryName': this.defaults.countryName,
          'languageCode': this.defaults.languageCode,
          'languageName': 'English',
          'storeCountryCode':'GB',
        }

        newState.states = this.states

        this.setLocale(newState)

        return
      }

      /**
       * Fetch the customers locale and set it in the state.
       * - This can then be used to trigger geolocation pop-ups, restrict content etc.
       * https://shopify.dev/docs/themes/markets/localization-discovery
       */
      const queryParams = new URLSearchParams({
        'country[enabled]': 'true',
        'language[enabled]': 'true',
      })

      /**
       * Use `mint.routes.normalisedRoot` to ensure that locale is fetched when using 'Preview' mode.
       * (https://[random-string].shopifypreview.com/products_preview?preview_key=[random-key])
       * - This is when admins click 'Preview' in the Admin -> Edit Product screen.
       */
      fetch(`${mint.routes.normalisedRoot}browsing_context_suggestions.json?${queryParams.toString()}`)
        .then((response) => response.json())
        .then((response) => {
          const detectedCountry = response.detected_values?.country
          const detectedLanguage = response.suggestions?.length
            ? response.suggestions[0].parts?.language
            : false

          if (detectedCountry) {
            detected.countryCode = detectedCountry.handle
            detected.countryName = detectedCountry.name
          }

          /**
           * Detected language uses a combination of Browser language and IP.
           */
          if (detectedLanguage) {
            detected.languageCode = detectedLanguage.handle
            detected.languageName = detectedLanguage.name
          }

          newState.detected = detected
          newState.states = this.getUpdatedStates(detected, shouldClearCache)
        })
        .catch((error) => {

          /**
           * Set states to default locale upon failure.
           */
          newState.detected = this.detected
          newState.states = this.states

          const data = {
            detected: JSON.parse(JSON.stringify(this.detected)),
            states: JSON.parse(JSON.stringify(this.states)),
          }

          mint.Log(
            'error',
            'Unable to detect locale',
            error,
          )
        })
        .finally(() => {
          this.setLocale(newState)
        })
    },

    /**
     * Set relevant booleans and return to the state.
     * - Regardless of cache, recalculate this on page load.
     * @param {Object} detected - object of geolocation data from the customer
     * @param {Boolean} hasSwitchedMarkets - If the customer has recently selected a new market
     * @returns {Object}
     */
    getUpdatedStates(detected, hasSwitchedMarkets = false) {

      /**
       * If no detected country code, return defaults.
       */
      if (!detected.countryCode) {
        return this.states
      }

      const states = {}

      /**
       * The customer is already on the correct local store
       * for their detected location.
       */
      states.isCorrectMarket = Boolean(detected.countryCode === this.countryCode)

      /**
       * The customer is on the default local market for the store.
       */
      states.isDefaultMarket = Boolean(this.countryCode === this.defaults.countryCode)

      /**
       * The customers detected location is a supported local market.
       */
      states.isSupportedMarket = Boolean(
        mint.countries.find((market) => {
          return market.countryCode === detected.countryCode
        }),
      )

      /**
       * The customer has recently switched markets.
       * - Selection via dropdown selector
       * - Changed via URL parameter
       * - Allows to trigger 'Welcome' pop-ups.
       */
      states.hasSwitchedMarkets = hasSwitchedMarkets

      /**
       * Handle suggestions for localised markets.
       * - This is so that a customer can be redirected to a static subfolder
       *   if their geolocation matches.
       * - The state is updated for components to handle suggestions
       * - Note the below could/will change depending on UX, but this is a stat.
       */
      const subfolders = [
        'en-ie',
        'en-us',
      ]

      const targetSubfolder = `/en-${detected.countryCode.toLowerCase()}/`
      const customerHasLocalMarket = subfolders.includes(targetSubfolder)

      /**
       * Customer has been detected in a market of which we
       * have a specified subfolder for.
       */
      if (customerHasLocalMarket && !mint.shop.localisedShopUrl.includes(targetSubfolder)) {
        states.suggested.shouldSuggest = true
        states.suggested.target = targetSubfolder
      }

      return states
    },

    /**
     * Store the locale within the state.
     * - Emits state to other Vue instances.
     * @param {Object} newLocale - Formatted locale object
     * @param {Boolean} isSection - The method is being run outside of mint-core.js
     */
    setLocale(newLocale, isSection) {
      this.detected = newLocale.detected
      this.states = newLocale.states
      this.states.locationSet = true

      /**
       * - mint-section.js and mint-components.js listens to the
       * EventBus listener at the end of this method.
       * - Early return to prevent endless loop.
       */
      if (isSection) {
        return
      }

      /**
        * This converts the ES6 Proxy State to a plain object.
        * (Hoping there is a better way)
        */
      const state = JSON.parse(JSON.stringify(this.$state))

      /**
       * Used to synchronise global state between sections
       */
      sessionStorage.setItem(
        storageNamespace.pinia.locale,
        JSON.stringify(state),
      )

      /**
       * Used for caching, as it's typically unlikely the
       *   customer will be switching geolocation frequently.
       * - Checks within `queryLocale` will account for store-switching
       *   and flushing the cache.
       */
      Cookies.set(
        cookieNamespace.geolocation,
        JSON.stringify(state.detected),
        { expires: 7 },
      )

      /**
       * Notify other sections and update their Pinia state.
       */
      setTimeout(() => {
        mint.EventBus.dispatch('mint:locale:set', state)
      })
    },
  },
})
